// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { 
  getFirestore, 
  connectFirestoreEmulator, 
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
  disableNetwork,
  enableNetwork
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

/**
 * White Label Firebase Configuration
 * 
 * This setup allows easy switching between different Firebase projects
 * for white labeling. Brand-specific Firebase projects can be configured
 * through environment variables or custom config files.
 */

// SICHERHEIT: Keine API-Schlüssel im Code hinterlegen
// Alle Werte müssen durch Umgebungsvariablen bereitgestellt werden
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Prüfen ob alle erforderlichen Konfigurationswerte vorhanden sind
const validateFirebaseConfig = (config) => {
  const requiredKeys = [
    'apiKey', 'authDomain', 'projectId', 'storageBucket', 
    'messagingSenderId', 'appId'
  ];
  
  const missingKeys = requiredKeys.filter(key => !config[key]);
  
  if (missingKeys.length > 0) {
    console.error(
      `Firebase-Konfiguration unvollständig! Fehlende Schlüssel: ${missingKeys.join(', ')}\n` +
      `Bitte erstellen Sie eine .env.local Datei mit den erforderlichen REACT_APP_FIREBASE_* Umgebungsvariablen.`
    );
    return false;
  }
  
  return true;
};

// Firebase nur initialisieren, wenn die Konfiguration gültig ist
let app, auth, db, storage, functions, analytics;

if (validateFirebaseConfig(firebaseConfig)) {
  try {
    // Initialize Firebase
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    
    // Moderne Firestore-Initialisierung mit cache-Einstellungen
    try {
      // Verwende moderne API mit cache-Einstellungen
      db = initializeFirestore(app, {
        cache: {
          sizeBytes: CACHE_SIZE_UNLIMITED,
          tabManager: 'automatic'
        }
      });
      console.log('Firestore mit moderner cache-Konfiguration initialisiert');
    } catch (error) {
      // Fallback auf Standard-Initialisierung ohne deprecated APIs
      console.warn('Moderne cache-Konfiguration nicht verfügbar, verwende Standard-Initialisierung:', error.message);
      db = getFirestore(app);
      console.log('Firestore mit Standard-Konfiguration initialisiert');
    }
    
    storage = getStorage(app);
    functions = getFunctions(app, process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'europe-west3');
    
    // Google Analytics bewusst NICHT starten: Cookie-Banner und Datenschutz-
    // erklärung sagen zu, dass keine Analyse-Cookies von Drittanbietern gesetzt
    // werden, und das Portal wertet keine Analytics-Ereignisse aus. Vor einer
    // Aktivierung müsste der Start an die Einwilligung im Banner gekoppelt und
    // die Datenschutzerklärung ergänzt werden (§ 25 TDDDG).
    analytics = null;
    
    // Log Firebase initialization status
    console.log(`Firebase erfolgreich initialisiert (Umgebung: ${process.env.NODE_ENV})`);
    
    // Listener für Online/Offline-Status
    if (typeof window !== 'undefined') {
      // Netzwerkstatus für Firebase explizit setzen bei Initialisierung
      if (navigator.onLine) {
        enableNetwork(db).catch(err => console.error('Fehler beim Aktivieren des Netzwerks bei Initialisierung:', err));
      }
      
      // Event-Listener für Online-Status mit Verzögerung zur Stabilisierung
      window.addEventListener('online', () => {
        console.log('Netzwerkverbindung wiederhergestellt, aktiviere Firestore-Netzwerk');
        // Kurze Verzögerung zur Stabilisierung der Verbindung
        setTimeout(() => {
          enableNetwork(db).catch(err => console.error('Fehler beim Aktivieren des Netzwerks:', err));
        }, 1000);
      });
      
      window.addEventListener('offline', () => {
        console.log('Netzwerkverbindung verloren, deaktiviere Firestore-Netzwerk');
        disableNetwork(db).catch(err => console.error('Fehler beim Deaktivieren des Netzwerks:', err));
      });
    }
  
    // Verwende Emulatoren in der Entwicklungsumgebung
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      // Aktiviere Emulatoren nur, wenn die entsprechenden Umgebungsvariablen gesetzt sind
      if (process.env.REACT_APP_USE_AUTH_EMULATOR === 'true') {
        connectAuthEmulator(auth, 'http://localhost:9099');
      }
      
      if (process.env.REACT_APP_USE_FIRESTORE_EMULATOR === 'true') {
        connectFirestoreEmulator(db, 'localhost', 8080);
      }
      
      if (process.env.REACT_APP_USE_STORAGE_EMULATOR === 'true') {
        connectStorageEmulator(storage, 'localhost', 9199);
      }
      
      if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true') {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
    }
  } catch (error) {
    console.error('Firebase-Initialisierungsfehler:', error);
  }
} else {
  console.error('Firebase wurde nicht initialisiert aufgrund fehlender Konfiguration');
}

// Hilfreiche Utils-Funktion für Timestamp-Konvertierungen
const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  if (timestamp.toDate) {
    return timestamp.toDate(); // Firebase Timestamp zu JS Date
  }
  
  if (typeof timestamp === 'string') {
    return new Date(timestamp); // ISO String zu JS Date
  }
  
  return timestamp; // Falls es bereits ein Date-Objekt ist
};

export { app, auth, db, storage, functions, analytics, formatTimestamp };
export default firebaseConfig;
